// Guaranty Multi-Signer — the loan's guaranty roster + "Send guaranties"
// action, rendered under the Parties▸Guarantors section. Reads the roster from
// the loan's guarantor assignments (each row = one guarantor, its signing mode,
// and its status Pending → Sent → Signed → Filed) and fires one DocuSign envelope
// per Pending guarantor. Send is idempotent — re-invoking after a late-add only
// mails the new guarantor. Self-gates the Send button on the Collection.SendPackages
// permission (the same gate the API enforces).

import { Link } from "react-router-dom";
import { Button, Card } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import {
  useGuaranties,
  useSendGuaranties,
} from "@/hooks/portfolioAdmin/useGuaranties";
import type { GuarantyStatus } from "@/api/portfolioAdmin/guarantiesApi";

const STATUS_TONE: Record<GuarantyStatus, string> = {
  Pending: "text-gray-mid",
  Sent: "text-onyx",
  Delivered: "text-onyx",
  Signed: "text-status-success",
  Filed: "text-status-success",
  Declined: "text-status-error",
  Voided: "text-status-error",
};

function StatusPill({ status }: { status: GuarantyStatus }) {
  return (
    <span
      className={`rounded-full border border-border-light bg-white-warm px-space-2 py-[1px] text-caption font-normal ${STATUS_TONE[status] ?? "text-gray-mid"}`}
    >
      {status}
    </span>
  );
}

export function GuarantySendPanel({ loanId }: { loanId: string }) {
  const auth = useAuth();
  const toast = useToast();
  const { data, isLoading } = useGuaranties(loanId);
  const send = useSendGuaranties(loanId);
  const canSend =
    auth.hasPermission("Collection.SendPackages") || auth.hasRoleCode("ADMIN");

  const rows = data?.rows ?? [];
  const sendable = data?.sendableCount ?? 0;

  const onSend = async () => {
    try {
      const result = await send.mutateAsync();
      const inProgress = result.rows.filter((r) => r.status !== "Pending").length;
      toast.success(
        inProgress > 0
          ? `Guaranties sent — ${inProgress} guarantor${inProgress === 1 ? "" : "s"} in progress.`
          : "No guarantors to send. Add guarantors above first.",
      );
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Card data-testid="guaranty-send-panel">
      <div className="flex items-start justify-between gap-space-3">
        <div>
          <h3 className="font-display font-bold text-h4 m-0">Guaranty signing</h3>
          <p className="text-caption text-gray-mid mt-space-1 m-0">
            One DocuSign envelope per guarantor. The borrower signs in the portal;
            co-guarantors sign by email. Add a guarantor above and send again to
            include them.
          </p>
        </div>
        {canSend && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => void onSend()}
            disabled={send.isPending || rows.length === 0}
            data-testid="guaranty-send-button"
          >
            {send.isPending
              ? "Sending…"
              : sendable > 0
                ? `Send guaranties (${sendable})`
                : "Send guaranties"}
          </Button>
        )}
      </div>

      {isLoading ? (
        <p className="text-small text-gray-mid italic mt-space-3 m-0">
          Loading guaranty roster…
        </p>
      ) : rows.length === 0 ? (
        <p className="text-small text-gray-mid mt-space-3 m-0" data-testid="guaranty-empty">
          No guarantors on this loan yet. Add guarantors in the section above,
          then send.
        </p>
      ) : (
        <ul className="mt-space-3 flex flex-col gap-space-2 list-none p-0 m-0">
          {rows.map((r) => (
            <li
              key={r.id}
              className="flex items-center justify-between gap-space-3 border-b border-border-light pb-space-2 last:border-b-0"
              data-testid="guaranty-row"
            >
              <div className="flex flex-col">
                <span className="text-small text-onyx">{r.guarantorName}</span>
                <span className="text-caption text-gray-mid">{r.guarantorEmail}</span>
              </div>
              <div className="flex items-center gap-space-2">
                <span className="text-caption uppercase text-gray-mid">
                  {r.signingMode === "Embedded" ? "In portal" : "By email"}
                </span>
                <StatusPill status={r.status} />
                {r.filedDocumentId && (
                  <Link
                    to={`/portfolio-admin/loans/${loanId}?tab=documents`}
                    className="text-caption text-gold-shadow hover:underline"
                  >
                    View filed
                  </Link>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
